import { AxiosError } from 'axios';
import { ApiError, ExtendedError, ErrorSeverity } from './api.types';

// Base API Error class
export class ApiErrorBase extends Error {
  public code: string;
  public severity: ErrorSeverity;
  public timestamp: string;
  public context?: Record<string, any>;

  constructor(
    message: string,
    code: string,
    severity: ErrorSeverity = 'error',
    context?: Record<string, any>
  ) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.severity = severity;
    this.timestamp = new Date().toISOString();
    this.context = context;
    
    // Maintains proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  toJSON(): ExtendedError {
    return {
      message: this.message,
      code: this.code,
      severity: this.severity,
      timestamp: this.timestamp,
      context: this.context,
      stackTrace: this.stack,
    };
  }
}

// Specific error classes
export class AuthenticationError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'AUTH_FAILED', 'error', context);
  }
}

export class AuthorizationError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'FORBIDDEN', 'error', context);
  }
}

export class ValidationError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'VALIDATION_ERROR', 'warning', context);
  }
}

export class NotFoundError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'NOT_FOUND', 'warning', context);
  }
}

export class ConflictError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'CONFLICT', 'warning', context);
  }
}

export class ServerError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'SERVER_ERROR', 'critical', context);
  }
}

export class NetworkError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'NETWORK_ERROR', 'error', context);
  }
}

export class TimeoutError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'TIMEOUT', 'error', context);
  }
}

export class RateLimitError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'RATE_LIMIT', 'warning', context);
  }
}

export class ApiVersionError extends ApiErrorBase {
  constructor(message: string, context?: Record<string, any>) {
    super(message, 'API_VERSION_MISMATCH', 'critical', context);
  }
}

// Error code to user message mapping
const ERROR_MESSAGES: Record<string, string> = {
  'AUTH_FAILED': 'Please log in again',
  'FORBIDDEN': 'You don\'t have permission to perform this action',
  'VALIDATION_ERROR': 'Please check your input and try again',
  'NOT_FOUND': 'The requested resource was not found',
  'CONFLICT': 'This action conflicts with existing data',
  'SERVER_ERROR': 'Something went wrong on our end. Please try again later',
  'NETWORK_ERROR': 'Unable to connect to the server. Please check your internet connection',
  'TIMEOUT': 'The request took too long. Please try again',
  'RATE_LIMIT': 'Too many requests, please wait',
  'API_VERSION_MISMATCH': 'Your app version is outdated. Please refresh the page',
  'UNKNOWN_ERROR': 'An unexpected error occurred. Please try again',
};

// Parse Axios error to custom error
export function parseApiError(error: AxiosError<ApiError>): ApiErrorBase {
  // Network or timeout errors
  if (!error.response) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      return new TimeoutError(
        ERROR_MESSAGES.TIMEOUT,
        { originalError: error.message }
      );
    }
    return new NetworkError(
      ERROR_MESSAGES.NETWORK_ERROR,
      { originalError: error.message }
    );
  }

  const status = error.response.status;
  const data = error.response.data;
  const errorMessage = data?.error || error.message;
  const errorCode = data?.code;

  // Check for API version mismatch
  const apiVersion = error.response.headers['x-api-version'];
  const expectedVersion = import.meta.env.VITE_API_VERSION || 'v1';
  if (apiVersion && apiVersion !== expectedVersion) {
    return new ApiVersionError(
      ERROR_MESSAGES.API_VERSION_MISMATCH,
      { expected: expectedVersion, received: apiVersion }
    );
  }

  // Map status codes to error classes
  switch (status) {
    case 401:
      return new AuthenticationError(
        ERROR_MESSAGES.AUTH_FAILED,
        { originalError: errorMessage }
      );
    
    case 403:
      return new AuthorizationError(
        ERROR_MESSAGES.FORBIDDEN,
        { originalError: errorMessage }
      );
    
    case 400:
      return new ValidationError(
        getUserFriendlyMessage(errorCode) || errorMessage,
        { details: data?.details }
      );
    
    case 404:
      return new NotFoundError(
        ERROR_MESSAGES.NOT_FOUND,
        { resource: error.config?.url }
      );
    
    case 409:
      return new ConflictError(
        getUserFriendlyMessage(errorCode) || errorMessage,
        { details: data?.details }
      );
    
    case 429:
      const retryAfter = error.response.headers['retry-after'];
      return new RateLimitError(
        ERROR_MESSAGES.RATE_LIMIT,
        { retryAfter: retryAfter ? parseInt(retryAfter) : undefined }
      );
    
    case 500:
    case 502:
    case 503:
    case 504:
      return new ServerError(
        ERROR_MESSAGES.SERVER_ERROR,
        { status, originalError: errorMessage }
      );
    
    default:
      return new ApiErrorBase(
        getUserFriendlyMessage(errorCode) || ERROR_MESSAGES.UNKNOWN_ERROR,
        errorCode || 'UNKNOWN_ERROR',
        'error',
        { status, originalError: errorMessage }
      );
  }
}

// Get user-friendly error message
export function getUserFriendlyMessage(errorCode?: string): string | null {
  if (!errorCode) return null;
  return ERROR_MESSAGES[errorCode] || null;
}

// Check if error is retryable
export function isRetryableError(error: ApiErrorBase): boolean {
  return (
    error instanceof NetworkError ||
    error instanceof TimeoutError ||
    error instanceof ServerError ||
    (error instanceof RateLimitError && error.context?.retryAfter)
  );
}

// Get retry delay for error
export function getRetryDelay(error: ApiErrorBase, attemptNumber: number): number {
  if (error instanceof RateLimitError && error.context?.retryAfter) {
    return error.context.retryAfter * 1000;
  }
  
  // Exponential backoff with jitter
  const baseDelay = 1000;
  const maxDelay = 30000;
  const delay = Math.min(baseDelay * Math.pow(2, attemptNumber), maxDelay);
  const jitter = delay * 0.1 * Math.random();
  
  return delay + jitter;
}